import {
  Mesh,
  MeshBasicMaterial,
  Shape,
  ShapeGeometry,
  Vector2,
  Vector3,
  Texture,
  Object3D,
  TextureLoader,
  RepeatWrapping,
} from "three";

import SpriteText from "three-spritetext";
import { Line2 } from "three/examples/jsm/lines/Line2";
import { LineMaterial } from "three/examples/jsm/lines/LineMaterial";
import { LineGeometry } from "three/examples/jsm/lines/LineGeometry";
import { hexToRgb, rgbToHex } from "./utils";

const defaultColor = "#545454";

interface ZoneDataEvent extends Zone {
  barycentre: {
    x: number;
    y: number;
  };
}

interface ZoneOverEvent {
  event: any;
  data: ZoneDataEvent;
}

interface BasicStyle {
  color?: string;
  opacity?: number;
}

interface BasicShapeStyle extends BasicStyle {
  textureUrl?: string;
}

interface ShapeStyle extends BasicShapeStyle {
  active?: {
    opacity?: number;
    blink?: string;
  };
}

const defaultShapeStyle: BasicShapeStyle = {
  color: defaultColor,
  opacity: 0.8,
};

interface BasicLineStyle {
  color?: string;
  linewidth?: number;
  resolution?: Vector2;
  dashed?: boolean;
}

interface LineStyle extends BasicLineStyle {}

const defaultLineStyle: BasicLineStyle = {
  color: defaultColor,
  linewidth: 2,
  resolution: new Vector2(1920, 1080),
  dashed: false,
};

interface LabelStyle {
  fontFace?: string;
  fontWeight?: string;
  textHeight?: number;
  color?: string;
  backgroundColor?: string;
  strokeWidth?: number;
  fontSize?: number;
  padding?: number;
  borderWidth?: number;
  borderRadius?: number;
  borderColor?: string;
}

const defaultLabelStyle: LabelStyle = {
  fontFace: "Arial",
  fontWeight: "bolder",
  textHeight: 30,
  color: "#444444",
  backgroundColor: "#eeeeee",
  strokeWidth: 0,
  fontSize: 120,
  padding: 5,
  borderWidth: 0.5,
  borderRadius: 8,
  borderColor: "#444444",
};

interface ZoneStyle extends ShapeStyle {
  line?: LineStyle | boolean;
  label?: LabelStyle | boolean;
}

type Coordinates = [number, number, number];
type ZoneCoords = Coordinates[][];

interface ZoneParams {
  key: string;
  label?: string;
  coords: ZoneCoords;
  data: object;
  style: ZoneStyle;
}

const blinkPeriod = 1200; // periode en ms de l'effet de clignottement;
class Zone {
  public readonly type = "Zone";
  readonly data: any;
  readonly key: string;
  readonly style: ZoneStyle;
  private isActive: boolean;
  readonly coords: Coordinates[][];
  public baryvector: Vector3;
  private shapeMesh: Mesh;
  private sprite: SpriteText;
  private lineMesh: Mesh;
  private initialized: boolean;
  readonly label: string;
  private onChange: () => void;
  static textureLoader: TextureLoader;
  static cache: { [key: string]: Texture } = {};
  private blinkStartTime: number;

  constructor(params: ZoneParams, onChange: () => void) {
    this.data = params.data;
    this.key = params.key;
    this.coords = params.coords;
    this.style = params.style;
    this.label = params.label;
    this.isActive = false;
    this.onChange = onChange;
  }

  private initBaryvector() {
    const arr = [...new Array(3)];
    const barycentre = arr.map((a, i) => {
      const vals = this.coords[0].map((v) => v[i]);
      const min = Math.min(...vals);
      const max = Math.max(...vals);
      return (min + max) / 2;
    });
    barycentre[2] = this.coords[0][0][2];
    this.baryvector = new Vector3(...barycentre);
  }

  private getShapeMaterial(): MeshBasicMaterial {
    const style: BasicShapeStyle = {
      ...defaultShapeStyle,
      ...(this.style as BasicShapeStyle),
    };
    const material = new MeshBasicMaterial({
      color: style.color,
      transparent: true,
      opacity: style.opacity,
    });
    if (style.textureUrl) {
      let texture;
      if (Zone.cache[style.textureUrl]) {
        texture = Zone.cache[style.textureUrl];
      } else {
        texture = Zone.textureLoader.load(style.textureUrl);
        Zone.cache[style.textureUrl] = texture;
      }
      texture = Zone.textureLoader.load(style.textureUrl);
      texture.wrapS = texture.wrapT = RepeatWrapping;
      //texture.minFilter = NearestFilter;
      //texture.magFilter = NearestFilter;
      texture.repeat.set(0.05, 0.05);
      texture.rotation = 45;
      material.map = texture;
    }
    return material;
  }

  private getShapeGeometry(): ShapeGeometry {
    const pts = this.coords[0].map((p) => {
      return new Vector2(p[0], p[1]);
    });
    const shape = new Shape(pts);
    return new ShapeGeometry(shape);
  }

  private initShape() {
    const material = this.getShapeMaterial();
    const geometry = this.getShapeGeometry();
    const shape = new Mesh(geometry, material);
    shape.position.set(0, 0, this.coords[0][0][2]);
    shape.userData = { zone: this, type: "shape" };
    this.shapeMesh = shape;
  }

  private initSprite() {
    let style: LabelStyle = {
      ...defaultLabelStyle,
    };
    if (typeof this.style.label === "object") {
      style = { ...style, ...this.style.label };
    }
    const sprite = new SpriteText(this.label, style.textHeight) as {
      [index: string]: any;
    };
    const { x, y, z } = this.baryvector;

    for (let key in style) {
      sprite[key] = style[key as keyof typeof style];
    }
    sprite.userData = { zone: this, type: "sprite" };
    this.sprite = sprite as SpriteText;
    this.sprite.position.set(x, y, z + 30);
  }

  public setLabel(text: string): void {
    if (!this.sprite) return;
    this.sprite.text = text;
  }

  private getLineMaterial() {
    const lineStyle =
      typeof this.style.line === "object"
        ? this.style.line
        : { color: this.style.color };
    const style: LineStyle = {
      ...defaultLineStyle,
      ...lineStyle,
    };
    // Conversion format '#dddddd' to value 0xdddddd, as lineMaterial takes int values for color
    const color = parseInt(style.color.substring(1), 16);
    return new LineMaterial({
      color: color,
      linewidth: style.linewidth,
      vertexColors: true,
      dashed: style.dashed,
      alphaToCoverage: true,
      resolution: style.resolution,
    });
  }

  private getLineGeometry(): LineGeometry {
    const geometry = new LineGeometry();
    const flatten = this.coords.flat().flat();
    flatten.push(...this.coords[0][0]);
    geometry.setPositions(flatten);
    //todo: test 0.8 is an opacity
    const colors = new Array(flatten.length + 3).fill(0.8);
    geometry.setColors(colors);
    return geometry;
  }

  private initLine() {
    const geometry = this.getLineGeometry();
    const material = this.getLineMaterial();
    const line = new Line2(geometry, material);
    line.computeLineDistances();
    line.scale.set(1, 1, 1);
    line.userData = { zone: this, type: "line" };
    this.lineMesh = line;
  }

  init3d() {
    this.initBaryvector();
    this.initShape();
    if (this.style.line) {
      this.initLine();
    }
    if (this.label) {
      this.initSprite();
    }
    this.initialized = true;
  }

  get3d(): Object3D[] {
    if (!this.initialized) {
      this.init3d();
    }
    const meshes: Object3D[] = [this.shapeMesh];
    if (this.sprite) {
      meshes.push(this.sprite);
    }
    if (this.lineMesh) {
      meshes.push(this.lineMesh);
    }
    return meshes;
  }

  setSpriteVisible(val: boolean): void {
    if (this.sprite) {
      this.sprite.visible = val;
    }
  }

  setShapeVisible(val: boolean): void {
    this.shapeMesh.visible = val;
  }

  setLineVisible(val: boolean): void {
    if (this.lineMesh) {
      this.lineMesh.visible = val;
    }
  }

  setVisible(val: boolean): void {
    this.setShapeVisible(val);
    this.setSpriteVisible(val);
    this.setLineVisible(val);
  }

  setActive(val: boolean) {
    if (this.isActive === val) return;
    this.isActive = val;
    const material = <MeshBasicMaterial>this.shapeMesh.material;
    if (this.style.active.opacity) {
      material.opacity = this.isActive
        ? this.style.active.opacity
        : this.style.opacity;
      material.needsUpdate = true;
    }
    if (!val) {
      material.color.set(this.style.color);
      material.needsUpdate = true;
    }
    if (val && this.style.active.blink) {
      this.blinkStartTime = performance.now();
      requestAnimationFrame(this.blink.bind(this));
    }
    this.onChange();
  }

  blink(currentTime: number) {
    const startColorRgb = hexToRgb(this.style.color);
    const endColorRgb = hexToRgb(this.style.active.blink);
    const colorDiff = [
      endColorRgb[0] - startColorRgb[0],
      endColorRgb[1] - startColorRgb[1],
      endColorRgb[2] - startColorRgb[2],
    ];
    const colorChangePerMs: number[] = colorDiff.map(
      (diff) => diff / blinkPeriod
    );
    const elapsed = (currentTime - this.blinkStartTime) % blinkPeriod;
    let currentColor: [number, number, number];
    if (elapsed < blinkPeriod / 2) {
      currentColor = [
        startColorRgb[0] + colorChangePerMs[0] * elapsed * 2,
        startColorRgb[1] + colorChangePerMs[1] * elapsed * 2,
        startColorRgb[2] + colorChangePerMs[2] * elapsed * 2,
      ];
    } else {
      currentColor = [
        endColorRgb[0] - colorChangePerMs[0] * (elapsed - blinkPeriod / 2) * 2,
        endColorRgb[1] - colorChangePerMs[1] * (elapsed - blinkPeriod / 2) * 2,
        endColorRgb[2] - colorChangePerMs[2] * (elapsed - blinkPeriod / 2) * 2,
      ];
    }
    const currentColorHex = rgbToHex(currentColor);
    const material = <MeshBasicMaterial>this.shapeMesh.material;
    if (this.isActive) {
      material.color.set(currentColorHex);
      material.needsUpdate = true;
      this.onChange();
      requestAnimationFrame(this.blink.bind(this));
    }
  }
}

export {
  Zone,
  ZoneStyle,
  ZoneParams,
  BasicStyle,
  LineStyle,
  LabelStyle,
  Coordinates,
  ZoneCoords,
  ZoneOverEvent,
  ZoneDataEvent,
};
